<template>
	<header id="step-header" class="breadcrumb">
		<span>Dashboard</span>
		<div style="flex-grow: 1"></div>
		<NotificationBell v-if="showCrumbBell" placement="header" />
	</header>

	<!-- Messaggi di stato -->
	<p v-if="loading" class="msg">Caricamento…</p>
	<p v-else-if="error" class="msg error">
		{{ error }}
	</p>
	<p v-else-if="!devices.length && !loading && !error" class="msg">
		Nessun dispositivo presente
	</p>

	<div v-else class="dashboard-wrapper">
		<!-- Mappa -->
		<div
			id="step-map"
			class="map-container"
			:class="{ 'is-collapsed': mapCollapsed }"
		>
			<l-map
				ref="map"
				:zoom="zoom"
				:center="center"
				:options="{ zoomControl: false }"
				@ready="onMapReady"
			>
				<l-tile-layer
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					attribution="© OpenStreetMap contributors"
				/>

				<l-marker-cluster-group ref="clusterRef" :options="clusterOptions">
					<l-marker
						v-for="device in filteredDevicesWithLocation"
						:key="device.id"
						:lat-lng="device.location"
						:icon="getDeviceIcon(device.status, device)"
						:ref="(el) => setMarkerRef(el, device.id)"
						@popupopen="handlePopupOpen(device.id)"
						@popupclose="handlePopupClose"
					>
						<l-tooltip
							:ref="(el) => setTooltipRef(el, device.id)"
							:options="{
								permanent: true,
								direction: 'top',
								offset: [0, -40],
								opacity: 0.9,
								className: 'marker-tooltip',
								interactive: false,
							}"
						>
							{{ device.name || device.id }}
						</l-tooltip>
						<l-popup :options="{ className: 'device-frame' }">
							<!-- usa la classe “popup-card” che condivide gli stili col modal -->
							<div class="popup-card">
								<button class="popup-close" @click="closeActivePopup">
									<CloseIcon />
								</button>
								<h4>{{ device.name || device.id }}</h4>

								<p
									class="status"
									style="
										display: flex;
										align-items: center;
										justify-content: space-between;
									"
								>
									<span>
										<span
											class="status-dot"
											:class="getDeviceStatusClass(device)"
										></span>
										{{ device.status }}
									</span>

									<button class="btn-action-labeled" @click="openData(device)">
										<DataIcon />
										<span>Data</span>
									</button>
								</p>

								<div class="label-row" v-if="getModuleLabels(device).length">
									<span
										v-for="m in getModuleLabels(device)"
										:key="m.key"
										:class="['label', `label--${m.key}`]"
										>{{ m.text }}</span
									>
								</div>
								<p v-else>Moduli connessi: —</p>

								<p>Ultimo aggiornamento: {{ formatDate(device.updatedAt) }}</p>

								<div
									class="popup-actions"
									style="margin-top: 8px; display: flex; gap: 8px"
								>
									<button @click="centerMapOn(device.location)">
										Centra mappa
									</button>
								</div>
							</div>
						</l-popup>
					</l-marker>
				</l-marker-cluster-group>
			</l-map>

			<!-- pill aggiorna posizioni -->
			<button
				v-if="hasHaystackKeys"
				class="map-refresh-pill"
				:class="{ updating: isUpdating }"
				:disabled="isUpdating"
				@click="fetchAndDecrypt"
				title="Aggiorna posizioni"
			>
				<svg class="pill-icon" :class="{ spin: isUpdating }" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
					<path d="M21 12a9 9 0 1 1-6.219-8.56"/>
				</svg>
				<span class="pill-label">{{ isUpdating ? 'Aggiornamento…' : 'Aggiorna posizioni' }}</span>
			</button>

			<!-- bottone per tornare su/espandere -->
			<button
				class="map-fab"
				@click="toggleMap"
				:title="mapCollapsed ? 'Espandi mappa' : 'Contrai mappa'"
				:aria-label="mapCollapsed ? 'Espandi mappa' : 'Contrai mappa'"
			>
				<ChevronDownIcon v-if="mapCollapsed" />
				<ChevronUpIcon v-else />
			</button>
		</div>

		<!-- Tabella dispositivi -->
		<div id="step-table" class="tbl-wrap" v-show="devices.length">
			<table class="tbl">
				<thead>
					<tr>
						<th @click="toggleSort('id')" class="sortable">
							<div class="header-content">
								ID
								<component
									:is="
										sortBy === 'id'
											? sortOrder === 'asc'
												? ChevronUpIcon
												: ChevronDownIcon
											: 'span'
									"
									class="sort-icon"
								/>
							</div>
						</th>
						<th>Nome</th>
						<th></th>
						<th>Status</th>
						<th>Dinamico/Statico</th>
						<th>Moduli connessi</th>
						<th>Posizione attuale</th>
						<th @click="toggleSort('updatedAt')" class="sortable">
							<div class="header-content">
								Last update
								<component
									:is="
										sortBy === 'updatedAt'
											? sortOrder === 'asc'
												? ChevronUpIcon
												: ChevronDownIcon
											: 'span'
									"
									class="sort-icon"
								/>
							</div>
						</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					<!-- Riga di ricerca -->
					<tr class="search-row">
						<td colspan="9">
							<div class="search-container">
								<input
									v-model="searchQuery"
									type="text"
									placeholder="Cerca per ID, Nome o Status..."
									class="search-input"
									@input="currentPage = 1"
								/>
							</div>
						</td>
					</tr>
					<tr
						v-for="(d, i) in paginatedDevices"
						:key="d.id"
						:class="['device-row', i % 2 && 'alt']"
						@click="focusDevice(d.id)"
						tabindex="0"
					>
						<td class="mono">{{ d.id }}</td>
						<td>
							{{ d.name || "—" }}
							<span
								v-if="d.is_shared"
								class="shared-device-badge"
								:title="`Condiviso da ${d.shared_by_name || 'Admin'}`"
							>
								🔗 Condiviso
							</span>
						</td>
						<td class="actions">
							<button
								class="btn-action-labeled"
								title="Data"
								@click.stop="openData(d)"
							>
								<DataIcon />
								<span>Data</span>
							</button>
						</td>
						<td>
							<span class="status-dot" :class="getDeviceStatusClass(d)" />
							{{ d.status }}
						</td>
						<!-- <td>{{ d.is_static ? "Statico" : "Dinamico" }}</td> -->
						<td>
							<button
								class="status-btn"
								@click.stop="openStatusModal(d)"
								:title="`Cambia modalità (attuale: ${
									d.is_static ? 'Statico' : 'Dinamico'
								})`"
							>
								<font-awesome-icon
									v-if="d.is_static"
									:icon="faLocationCrosshairs"
									class="status-icon static"
								/>
								<font-awesome-icon
									v-else
									:icon="faArrowsUpDownLeftRight"
									class="status-icon dynamic"
								/>
								{{ d.is_static ? "Statico" : "Dinamico" }}
								<ChevronDownIcon class="status-edit-icon" />
							</button>
						</td>
						<td>
							<div class="label-row" v-if="getModuleLabels(d).length">
								<span
									v-for="m in getModuleLabels(d)"
									:key="m.key"
									:class="['label', `label--${m.key}`]"
									>{{ m.text }}</span
								>
							</div>
							<span v-else>—</span>
						</td>

						<td class="mono">
							<span
								v-if="getLatLng(d.location)"
								class="coords"
								@click.stop="centerIfPossible(d)"
							>
								{{ formatCoords(d.location) }}
							</span>
							<span v-else>—</span>
							<button
								v-if="!isMoakDevice(d)"
								class="edit-coords-btn"
								:title="getLatLng(d.location) ? 'Modifica coordinate' : 'Inserisci nuove coordinate'"
								@click.stop="openLocationModal(d)"
							>
								<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z"/></svg>
							</button>
							<button
								v-else
								class="edit-coords-btn"
								title="Aggiorna posizione da Moak"
								:disabled="isUpdating"
								@click.stop="fetchAndDecrypt"
							>
								<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="{ spin: isUpdating }"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
							</button>
						</td>
						<td>{{ formatDate(d.updatedAt) }}</td>
					</tr>
					<!-- Riga nessun risultato -->
					<tr v-if="searchQuery && !filteredDevices.length">
						<td
							colspan="9"
							class="msg-row"
							style="
								text-align: center;
								padding: 24px;
								color: #666;
								background: #fff;
							"
						>
							Nessun risultato trovato per "{{ searchQuery }}"
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<!-- Paginazione -->
		<div class="pagination-controls" v-if="totalPages > 1">
			<button
				:disabled="currentPage === 1"
				@click="currentPage = 1"
				class="btn-pagination"
				title="Vai alla prima pagina"
			>
				<FontAwesomeIcon :icon="faAnglesLeft" />
			</button>
			<button
				:disabled="currentPage === 1"
				@click="currentPage--"
				class="btn-pagination"
				title="Vai alla pagina precedente"
			>
				<FontAwesomeIcon :icon="faAngleLeft" />
			</button>

			<div class="pagination-jump">
				<span>Pagina</span>
				<input
					type="number"
					v-model="currentPageInput"
					@keyup.enter="jumpToPage"
					@blur="jumpToPage"
					min="1"
					:max="totalPages"
					class="jump-input"
				/>
				<span>di {{ totalPages }}</span>
			</div>

			<button
				:disabled="currentPage === totalPages"
				@click="currentPage++"
				class="btn-pagination"
				title="Vai alla pagina successiva"
			>
				<FontAwesomeIcon :icon="faAngleRight" />
			</button>
			<button
				:disabled="currentPage === totalPages"
				@click="currentPage = totalPages"
				class="btn-pagination"
				title="Vai all'ultima pagina"
			>
				<FontAwesomeIcon :icon="faAnglesRight" />
			</button>
		</div>
	</div>

	<!-- modale per cambio stato "Dinamico/Statico"-->
	<div v-if="showStatusModal" class="modal-mask">
		<div class="modal">
			<h3>Cambio modalità dispositivo</h3>
			<p style="margin: 6px 0 18px">
				Seleziona la nuova modalità per
				<strong>{{ selectedDevice?.name || selectedDevice?.id }}</strong>
			</p>

			<div class="status-options">
				<button
					v-for="status in statusOptions"
					:key="status.label"
					@click="selectStatus(status.value)"
					:class="{ active: tempStatus === status.value }"
				>
					<font-awesome-icon
						v-if="status.value === true"
						:icon="faLocationCrosshairs"
						class="status-icon static"
					/>
					<font-awesome-icon
						v-else
						:icon="faArrowsUpDownLeftRight"
						class="status-icon dynamic"
					/>
					{{ status.label }}
				</button>
			</div>

			<div class="modal-actions">
				<button
					:disabled="tempStatus === null || statusLoading"
					@click="confirmStatusChange"
				>
					Conferma
				</button>
				<button :disabled="statusLoading" @click="cancelStatusChange">
					Annulla
				</button>
			</div>
		</div>
	</div>

	<!-- Location Edit Modal -->
	<LocationModal
		v-if="showLocationModal"
		:deviceName="locationModalDevice?.name"
		:deviceId="locationModalDevice?.id"
		:location="locationModalDevice?.location"
		@close="showLocationModal = false"
		@copy="copyLocationCoords"
		@saved="(loc, updatedAt) => { if (locationModalDevice) { locationModalDevice.location = loc; locationModalDevice.updatedAt = updatedAt; } showLocationModal = false; }"
	/>

	<!-- Permission Request Modal -->
	<PermissionRequestModal
		v-if="showPermissionModal"
		:deviceId="permissionDevice?.id"
		:deviceName="permissionDevice?.name"
		:permissionType="failedPermissionType"
		@close="showPermissionModal = false"
	/>
</template>

<script setup lang="ts">
import axios from "@/axiosInstance";
import dayjs from "dayjs";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
// @ts-ignore
import { ec as EC } from "elliptic";
import { Buffer } from "buffer";
// Polyfill Buffer for browser if needed, though vite usually handles it, or use Uint8Array
globalThis.Buffer = Buffer;

import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
	faLocationCrosshairs,
	faArrowsUpDownLeftRight,
	faAngleLeft,
	faAngleRight,
	faAnglesLeft,
	faAnglesRight,
} from "@fortawesome/free-solid-svg-icons";

// Lucide icons
import {
	ChevronDown as ChevronDownIcon,
	ChevronUp as ChevronUpIcon,
	Database as DataIcon,
	X as CloseIcon,
} from "lucide-vue-next";
import { io } from "socket.io-client";
import router from "@/router";
import { WS_URL } from "@/config";
import {
	computed,
	inject,
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref,
	watch,
} from "vue";
import { useToast } from "vue-toast-notification";
import NotificationBell from "./NotificationBell.vue";

import PermissionRequestModal from "@/components/PermissionRequestModal.vue";
import LocationModal from "@/components/data/LocationModal.vue";

const isDesktop = inject<Ref<boolean>>("isDesktop")!;
const isSidebarOpen = inject<Ref<boolean>>("isSidebarOpen")!;

const mapCollapsed = ref(false);

// Location Edit Modal State
const showLocationModal = ref(false);
const locationModalDevice = ref<any | null>(null);

function openLocationModal(device: any) {
	locationModalDevice.value = device;
	showLocationModal.value = true;
}

async function copyLocationCoords() {
	if (!locationModalDevice.value?.location) return;
	const [lat, lng] = locationModalDevice.value.location;
	try {
		await navigator.clipboard.writeText(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
		toast.success("Coordinate copiate!", { duration: 2000 });
	} catch {
		toast.error("Impossibile copiare le coordinate.", { duration: 2000 });
	}
}

// Permission Modal State
const showPermissionModal = ref(false);
const permissionDevice = ref<any | null>(null);
const failedPermissionType = ref<
	"change_status" | "change_mode" | "change_thresholds"
>("change_mode");

// soglie con isteresi
const COLLAPSE_AT = isDesktop.value ? 260 : 180;
const EXPAND_AT = isDesktop.value ? 120 : 60;

let ticking = false;

const showCrumbBell = computed(
	() => !isDesktop.value || (isDesktop.value && !isSidebarOpen.value),
);

/* ─── stato utente ─── */
const me = ref<any>({});
const isAdmin = computed(() => me.value.is_admin);

/* ---------------- websocket ---------------- */
const socket = io(WS_URL || "http://localhost:8080", {
	withCredentials: true,
	transports: ["websocket"],
});

socket.on("connect", () => socket.id);

// socket.on("graph:location", (p) => {
//   const idx = devices.value.findIndex((d) => d.id === p.id);
//   if (idx === -1) return;

//   const current = devices.value[idx];

//   // ⛔️ Non aggiornare la posizione se non è dinamico+online
//   if (!isMovable(current)) return;

//   // quando ricevi un device aggiornato o cambi manualmente lo status:
//   const updated = {
//     ...current,
//     status: p.status ?? current.status,
//     // forza statico in UI se non online (coerente col server)
//     is_static:
//       (p.status ?? current.status) !== "online" ? true : current.is_static,
//     location: p.location ?? current.location,
//     updatedAt: p.updated_at ?? current.updatedAt,
//   };

//   // sostituisci l’elemento dell’array (trigger reattivo garantito)
//   devices.value.splice(idx, 1, updated);

//   /* ---------  sposta il marker già montato  --------- */
//   const mk = markerRefs.get(p.id) as MarkerComponent | undefined;
//   if (mk?.leafletObject) {
//     mk.leafletObject.setLatLng(p.location);
//     clusterRef.value?.leafletObject.refreshClusters(mk.leafletObject);
//   }
// });

// Leaflet
globalThis.L = L; // richiesto dal plugin vue-leaflet-markercluster

import "leaflet.markercluster"; // 3️⃣  estende L con MarkerClusterGroup
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

// Vue-Leaflet components
import {
	LIcon,
	LMap,
	LMarker,
	LPopup,
	LTileLayer,
	LTooltip,
} from "@vue-leaflet/vue-leaflet";
import { LMarkerClusterGroup } from "vue-leaflet-markercluster";
import "vue-leaflet-markercluster/dist/style.css";

// Fix per le icone mancanti in produzione
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
	iconUrl: require("leaflet/dist/images/marker-icon.png"),
	shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const toast = useToast();
const loading = ref(true);
const devices = ref<any[]>([]);
const error = ref<string | null>(null);
const map = ref<any>(null);

// ref per il modale di cambio stato
const showStatusModal = ref(false);
const selectedDevice = ref<any | null>(null);
const tempStatus = ref<boolean | null>(null);
const statusLoading = ref(false);

const statusOptions = [
	{ label: "Dinamico", value: false, class: "on" },
	{ label: "Statico", value: true, class: "off" },
];

const isUpdating = ref(false);

/* ─── Ricerca e Paginazione ─── */
const searchQuery = ref("");
const currentPage = ref(1);
const itemsPerPage = ref(10);
const currentPageInput = ref(1);

const sortBy = ref("updatedAt");
const sortOrder = ref<"asc" | "desc">("desc");

watch(currentPage, (val) => {
	currentPageInput.value = val;
});

const toggleSort = (key: string) => {
	if (sortBy.value === key) {
		sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
	} else {
		sortBy.value = key;
		sortOrder.value = "asc";
	}
};

const jumpToPage = () => {
	let p = parseInt(String(currentPageInput.value));
	if (isNaN(p)) {
		currentPageInput.value = currentPage.value;
		return;
	}
	if (p < 1) p = 1;
	if (p > totalPages.value) p = totalPages.value;
	currentPage.value = p;
	currentPageInput.value = p;
};

const filteredDevices = computed(() => {
	if (!searchQuery.value) return devices.value;
	const q = searchQuery.value.toLowerCase();
	return devices.value.filter((d) => {
		const mode = d.is_static ? "statico" : "dinamico";
		return (
			d.id.toString().toLowerCase().includes(q) ||
			(d.name && d.name.toLowerCase().includes(q)) ||
			(d.status && d.status.toLowerCase().includes(q)) ||
			mode.includes(q)
		);
	});
});

const sortedDevices = computed(() => {
	const result = [...filteredDevices.value];
	return result.sort((a, b) => {
		let valA = a[sortBy.value];
		let valB = b[sortBy.value];

		if (sortBy.value === "updatedAt") {
			valA = valA ? new Date(valA).getTime() : 0;
			valB = valB ? new Date(valB).getTime() : 0;
		}

		if (valA < valB) return sortOrder.value === "asc" ? -1 : 1;
		if (valA > valB) return sortOrder.value === "asc" ? 1 : -1;
		return 0;
	});
});

const paginatedDevices = computed(() => {
	const start = (currentPage.value - 1) * itemsPerPage.value;
	const end = start + itemsPerPage.value;
	return sortedDevices.value.slice(start, end);
});

const totalPages = computed(() => {
	return Math.ceil(filteredDevices.value.length / itemsPerPage.value);
});

// Aggiunge coordinate ai dispositivi filtrati per la mappa
const filteredDevicesWithLocation = computed(() =>
	filteredDevices.value.filter(
		(d) => Array.isArray(d.location) && d.location.length === 2,
	),
);

// Watch for changes in filtered devices to re-center the map
watch(filteredDevicesWithLocation, () => {
	tryFitBounds(true);
});

// Haystack keys removed - now stored securely in backend API
const hasHaystackKeys = computed(() => true); // Backend will handle key availability

// Configurazione mappa
const center = ref<[number, number]>([45.41, 11.88]);
const zoom = ref(5);
const initialBoundsSet = ref(false); // 👈 nuova ref per auto-zoom

import type { LatLng, Marker as LeafletMarker } from "leaflet";
import type { ComponentPublicInstance, Ref } from "vue";

/** istanza Vue di <l-marker> con accessor leafletObject tipizzato */
type MarkerComponent = ComponentPublicInstance & {
	leafletObject: LeafletMarker;
};

/** istanza Vue di <l-marker-cluster-group> */
type ClusterComponent = ComponentPublicInstance & {
	leafletObject: L.MarkerClusterGroup;
};

// reference per mappa cluster dei marker
const clusterRef = ref<ClusterComponent | null>(null);
// reference per mappa dei tooltip
const tooltipRefs = new Map<string, any>();

const activePopupId = ref<string | null>(null);

// Configurazione cluster
const clusterOptions = {
	spiderfyOnMaxZoom: true,
	showCoverageOnHover: false,
	zoomToBoundsOnClick: true,
	maxClusterRadius: 60,
	iconCreateFunction: (cluster: any) => {
		return L.divIcon({
			html: `<span>${cluster.getChildCount()}</span>`,
			className: "marker-cluster",
			iconSize: L.point(40, 40, true),
		});
	},
};

// URL icone
const iconUrls = {
	online:
		"https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
	offline:
		"https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
	maintenance:
		"https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png",
	error:
		"https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-violet.png",
	default:
		"https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-grey.png",
};

const shadowUrl =
	"https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png";

// Aggiunge coordinate ai dispositivi
const devicesWithLocation = computed(() =>
	devices.value.filter(
		(d) => Array.isArray(d.location) && d.location.length === 2,
	),
);

// Toggles map state manually
function toggleMap() {
	mapCollapsed.value = !mapCollapsed.value;
}

// funzione che permette al marker di muoversi => salvare position, solo se NON statico e status esattamente 'online'
function isMovable(d: any): boolean {
	// ⛔️ NUCLEAR OPTION: Disabilita movimento per tutti, per evitare ghost movement
	// return d && d.is_static === false && d.status === "online";
	return false;
}

/** mappa id → componente <l-marker> */
const markerRefs = new Map<string, any>();

function setMarkerRef(el: any, id: string) {
	if (el) markerRefs.set(id, el as MarkerComponent); // mount
	else markerRefs.delete(id); // unmount
}

function setTooltipRef(el: any, id: string) {
	if (el) tooltipRefs.set(id, el); // mount
	else tooltipRefs.delete(id); // unmount
}

const handlePopupOpen = (deviceId: string) => {
	try {
		activePopupId.value = deviceId;
		const tt = tooltipRefs.get(deviceId);
		tt.leafletObject.getElement().style.display = "none"; // nasconde solo il suo
	} catch (e) {
		console.warn("Error handling popup open:", e);
	}
};

const handlePopupClose = () => {
	try {
		if (activePopupId.value) {
			const tt = tooltipRefs.get(activePopupId.value);
			tt.leafletObject.getElement().style.display = ""; // lo mostra di nuovo
		}
		activePopupId.value = null;
	} catch (e) {
		console.warn("Error handling popup close:", e);
	}
};

/** porta in primo piano il marker */
function focusDevice(deviceId: string) {
	const markerComp = markerRefs.get(deviceId);
	if (!markerComp) return;

	const marker = markerComp.leafletObject;
	const cluster = clusterRef.value?.leafletObject;

	// Chiudi tutti i tooltip prima di focalizzare
	tooltipRefs.forEach((tooltip) => {
		if (tooltip?.leafletObject) {
			tooltip.leafletObject.close();
		}
	});

	if (cluster) {
		cluster.zoomToShowLayer(marker, () => {
			map.value?.leafletObject.panTo(marker.getLatLng() as LatLng);
			// Apri il popup dopo il pan
			setTimeout(() => {
				marker.openPopup();
			}, 300);
		});
	} else {
		map.value?.leafletObject.panTo(marker.getLatLng() as LatLng);
		// Apri il popup dopo il pan
		setTimeout(() => {
			marker.openPopup();
		}, 300);
	}
}

// Crea icona per marker
const getDeviceIcon = (status: string, d?: any) => {
	const movable = d ? isMovable(d) : false;
	return new L.Icon({
		iconUrl: iconUrls[status as keyof typeof iconUrls] || iconUrls.default,
		shadowUrl,
		iconSize: [25, 41],
		iconAnchor: [12, 41],
		popupAnchor: [1, -34],
		className: `marker-icon-${status} ${movable ? "marker-movable" : ""}`,
	});
};

// Centra mappa su posizione
const centerMapOn = (latlng: [number, number]) => {
	if (map.value?.leafletObject) {
		map.value.leafletObject.flyTo(latlng, 12);
	}
	closeActivePopup(); // chiude popup attivi prima di centrare
};

// Callback quando la mappa è pronta
const onMapReady = () => {
	// Puoi accedere all'istanza Leaflet con map.value.leafletObject
	// Crea un pane specifico per i tooltip
	// map.value?.leafletObject.createPane("tooltipPane");
	tryFitBounds(); // 👈 Prova a centrare se i dati sono già pronti
};

const tryFitBounds = async (force = false) => {
	if (initialBoundsSet.value && !force) return;
	if (!filteredDevicesWithLocation.value.length) return;

	await nextTick();

	if (!map.value?.leafletObject) return;

	const validLocs = filteredDevicesWithLocation.value.map((d) => d.location);
	if (!validLocs.length) return;

	const bounds = L.latLngBounds(validLocs);
	if (bounds.isValid()) {
		map.value.leafletObject.fitBounds(bounds, {
			padding: [50, 50],
			maxZoom: 12,
		});
		initialBoundsSet.value = true;
	}
};

function closeActivePopup() {
	try {
		if (map.value?.leafletObject) {
			map.value.leafletObject.closePopup();
		}
		handlePopupClose();
	} catch (e) {
		console.warn("Error closing active popup:", e);
	}
}

// Funzioni di utilità
const getDeviceStatusClass = (d: any): string => {
	const status = d.status || "offline";

	// 1. Rosso se errore o manutenzione esplicita
	if (status === "error" || status === "maintenance") return "error";

	// 2. Arancione se ritado > 15 min
	const last = d.updatedAt ? new Date(d.updatedAt).getTime() : 0;
	const diff = Date.now() - last;
	// console.log("⏱️ Last update diff (ms):", diff);
	if (diff > 15 * 60 * 1000) return "maintenance"; // maintenance style = orange

	// 3. Mappatura standard
	const statusClasses: Record<string, string> = {
		online: "on",
		offline: "off",
		static: "off",
		dynamic: "on",
	};
	return statusClasses[status] || "off";
};

// New Keys, restored for static fetching matching
// New Keys, restored for static fetching matching
const HAYSTACK_KEYS = [
	{ name: "Moak 01 (1PYFKY)", id: "1PYFKY", color: "green", type: "moak" },
	{ name: "Moak 02 (XJZ1XD)", id: "XJZ1XD", color: "green", type: "moak" },
	{ name: "Moak 03 (B0C99B)", id: "B0C99B", color: "green", type: "moak" },
	{ name: "Moak 04 (0VH06Y)", id: "0VH06Y", color: "green", type: "moak" },
];

const HAYSTACK_IDS = HAYSTACK_KEYS.map((k) => k.id);
function isMoakDevice(device: any): boolean {
	if (!device?.name) return false;
	const upper = String(device.name).toUpperCase();
	return HAYSTACK_IDS.some((id) => upper.includes(id));
}

const fetchAndDecrypt = async () => {
	isUpdating.value = true;
	try {
		console.log("[Haystack] Fetching static positions from secure backend...");

		// Piccolo delay per rendere visibile l'animazione spin
		await new Promise((r) => setTimeout(r, 600));

		// Use configured axios (with JWT) to call our backend proxy
		// This keeps the external API private
		const res = await axios.get("/haystack/static-positions");
		const positions = res.data;

		let gotData = false;

		for (const keyDef of HAYSTACK_KEYS) {
			const posData = positions[keyDef.id];
			if (posData && posData.latitude && posData.longitude) {
				gotData = true;

				// Update existing or add new
				let existing = devices.value.find((d) => d.id === keyDef.id);

				// FALLBACK: Handle 0VH06Y vs OVH06Y typo in DB
				if (!existing && keyDef.id === "0VH06Y") {
					existing = devices.value.find((d) => d.id === "OVH06Y");
				}

				const ts = posData.timestamp || Date.now();
				if (existing) {
					existing.latitude = posData.latitude;
					existing.longitude = posData.longitude;
					existing.location = [posData.latitude, posData.longitude];
					existing.updatedAt = ts;
				}
			}
		}

		if (gotData) {
			toast.success(`Aggiornate posizioni Moak`);
		}
	} catch (e) {
		console.error("Update failed", e);
		toast.error("Errore durante l'aggiornamento posizioni");
	} finally {
		isUpdating.value = false;
	}
};

const formatDate = (iso: string) => {
	return dayjs(iso).format("DD/MM/YYYY HH:mm");
};

// const listModules = (d: any) => {
// 	return d.nodes?.map((n: any) => n.label).join(", ") || "—";
// };

// Normalizza i nomi dei moduli (da nodes[].label) in chiavi “stabili”
const normalizeModule = (raw: string): { key: string; text: string } => {
	const txt = (raw || "").trim();
	const s = txt.toLowerCase();

	// sinonimi → chiave
	if (/(mother\s*board|motherboard|mb)/.test(s))
		return { key: "motherboard", text: "Motherboard" };
	if (/lora/.test(s)) return { key: "lora", text: "LORa" };
	if (/lte\s*\/?\s*5g|5g|lte/.test(s)) return { key: "lte5g", text: "LTE/5G" };
	if (/sat|satellite/.test(s)) return { key: "sat", text: "SAT" };
	if (/wifi|wi\-?fi/.test(s)) return { key: "wifi", text: "Wi-Fi" };
	if (/ble|bluetooth/.test(s)) return { key: "ble", text: "BLE" };
	if (/gps|gnss/.test(s)) return { key: "gps", text: "GPS" };
	if (/camera|cam/.test(s)) return { key: "camera", text: "Camera" };
	if (/ai|ml|npu/.test(s)) return { key: "ai", text: "AI" };
	if (/battery|batt/.test(s)) return { key: "battery", text: "Battery" };
	if (/solar|pv|panel/.test(s)) return { key: "solar", text: "Solar" };
	if (/storage|sd|ssd|flash/.test(s))
		return { key: "storage", text: "Storage" };

	// fallback: chiave “slug” dal testo originale
	return {
		key: txt
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-|-$|/g, ""),
		text: raw,
	};
};

// Estrae moduli unici dal device (d.nodes[].label)
const getModuleLabels = (d: any): Array<{ key: string; text: string }> => {
	const src = d?.nodes?.map((n: any) => n?.label).filter(Boolean) || [];
	const map = new Map<string, { key: string; text: string }>();
	for (const label of src) {
		const mod = normalizeModule(String(label));
		if (!map.has(mod.key)) map.set(mod.key, mod);
	}
	return Array.from(map.values());
};

function getLatLng(loc: any): [number, number] | null {
	if (
		Array.isArray(loc) &&
		loc.length === 2 &&
		loc.every((n) => typeof n === "number")
	) {
		return [loc[0], loc[1]];
	}
	if (loc && typeof loc.lat === "number" && typeof loc.lng === "number") {
		return [loc.lat, loc.lng];
	}
	return null;
}

function formatCoords(loc: any): string {
	const ll = getLatLng(loc);
	if (!ll) return "—";
	const [lat, lng] = ll;
	return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

// opzionale: clic sulle coordinate per centrare la mappa
function centerIfPossible(d: any) {
	const ll = getLatLng(d.location);
	if (!ll) return;
	centerMapOn(ll as [number, number]);
}

const loadDevices = async () => {
	loading.value = true;
	try {
		// Carica info utente se non già caricate
		if (!me.value.id) {
			const { data: user } = await axios.get("/auth/me");
			me.value = user;
		}

		const { data, status } = await axios.get("/devices/get-all-graphs", {
			withCredentials: true,
		});

		if (status === 204) {
			devices.value = [];
			toast.info("Nessun dispositivo trovato", {
				position: "top-right",
				duration: 2000,
			});
			return;
		}

		// Filtra dispositivi attivi
		devices.value = (data ?? []).filter((g: any) => g.is_archived === false);

		// ──────────────── EXTERNAL STATIC POSITIONS (MOAK) ────────────────
		try {
			const extData = await fetchExternalStaticPositions();
			if (extData) {
				devices.value.forEach((d) => {
					// Solo per dispositivi statici
					if (!d.is_static) return;

					const candidates = [d.name, d.vin, d.serial_number, d.code].filter(
						Boolean,
					);

					let match = null;
					// Direct match on ID
					for (const c of candidates) {
						if (extData[c]) {
							match = extData[c];
							break;
						}
						// Partial match via key iteration
						const partialKey = Object.keys(extData).find((k) => c.includes(k));
						if (partialKey) {
							match = extData[partialKey];
							break;
						}
					}

					if (match && match.latitude && match.longitude) {
						d.location = [match.latitude, match.longitude];
						if (match.timestamp) {
							d.updatedAt = match.timestamp;
						} else {
							d.updatedAt = Date.now();
						}
					}
				});
			}
		} catch (e) {
			console.warn("Failed to merge external static positions", e);
		}
		// ──────────────────────────────────────────────────────────────────

		// ──────────────── DYNAMIC DEVICES HYDRATION ────────────────
		// Forza il refresh della posizione dall'ultimo log per TUTTI i device dinamici
		// per evitare disallineamenti con la tabella devices (che potrebbe avere dati vecchi).
		const dynamicDevices = devices.value.filter((d) => !d.is_static);

		if (dynamicDevices.length > 0) {
			console.log(
				`[Dashboard] Hydrating ${dynamicDevices.length} dynamic devices from logs...`,
			);

			await Promise.all(
				dynamicDevices.map(async (d) => {
					try {
						// Prima controlla se c'è latest_data (magari location era null ma i dati ci sono)
						if (d.latest_data && d.latest_data.latitude) {
							d.location = [d.latest_data.latitude, d.latest_data.longitude];
							if (d.latest_data.timestamp)
								d.updatedAt = d.latest_data.timestamp * 1000;
							return;
						}

						// Altrimenti chiamata API per storico
						const logsRes = await axios.get(`/track/${d.id}/logs`, {
							params: {
								limit: 1,
								orderBy: "timestamp",
								orderDirection: "DESC",
							},
						});
						const lastLog = logsRes.data?.logs?.[0];

						if (lastLog && lastLog.latitude && lastLog.longitude) {
							d.location = [lastLog.latitude, lastLog.longitude];
							// Se il timestamp arriva in secondi, converti in ms
							if (lastLog.timestamp) {
								d.updatedAt =
									lastLog.timestamp.toString().length === 10
										? lastLog.timestamp * 1000
										: lastLog.timestamp;
							}
							d.latest_data = lastLog;
							console.log(`[Dashboard] Recovered location for ${d.name}`);
						}
					} catch (e) {
						console.warn(
							`[Dashboard] Failed to recover location for ${d.name}`,
							e,
						);
					}
				}),
			);
		}
		// ────────────────────────────────────────────────────────

		// Auto-center mappa
		tryFitBounds();
	} catch (e: any) {
		error.value = e.response?.data?.details || "Errore caricamento";
		toast.error("Errore nel caricamento dei dispositivi", {
			position: "top-right",
			duration: 2000,
		});
	} finally {
		loading.value = false;
	}
};

/* ───────── External Data Fetcher ───────── */
async function fetchExternalStaticPositions() {
	try {
		// [FIX] Use backend proxy
		const res = await axios.get("/haystack/static-positions", {
			withCredentials: true,
		});
		return res.data;
	} catch (e) {
		console.warn("External static positions fetch failed", e);
		return null;
	}
}

function openData(device: any) {
	router.push({ name: "Data", params: { deviceId: device.id } });
}

function selectStatus(is_static: boolean) {
	tempStatus.value = is_static;
}

async function confirmStatusChange() {
	if (!selectedDevice.value || tempStatus.value === null) return;

	try {
		statusLoading.value = true;

		const { id, name } = selectedDevice.value;
		const payload = { is_static: tempStatus.value };

		await axios.patch(`/devices/${id}/change-is-static`, payload, {
			withCredentials: true,
		});

		// ✅ chiudi il modale prima del toast
		closeStatusModal();

		toast.success(
			`Il dispositivo ${name || id} ora è ${
				payload.is_static ? "Statico" : "Dinamico"
			}`,
			{ position: "top-right", duration: 2200 },
		);

		// Aggiorna subito lo stato locale per feedback immediato
		const dev = devices.value.find((d) => d.id === id);
		if (dev) {
			dev.is_static = payload.is_static;
		}

		// Ricarica la lista per coerenza e riapplica i fix statici
		await loadDevices();
		await fetchAndDecrypt();
	} catch (e: any) {
		// Check for permission error (401 or specific message)
		const isPermissionError =
			e.response?.status === 401 ||
			(e.response?.data?.message &&
				e.response.data.message.includes("Non hai i permessi"));

		if (isPermissionError) {
			// Capture device BEFORE closing the modal (which clears selectedDevice)
			permissionDevice.value = selectedDevice.value;
			failedPermissionType.value = "change_mode";

			closeStatusModal();

			// Show error toast to explain WHY the modal opened (User Request)
			const msg = extractServerError(e);
			toast.error(msg, { position: "top-right", duration: 4000 });

			// Open Request Modal with delay
			setTimeout(() => {
				showPermissionModal.value = true;
			}, 2500);
			return;
		}

		// ✅ messaggio preciso dal server + chiusura modale
		const msg = extractServerError(e);
		closeStatusModal();

		// Mostra il toast con un leggero ritardo (come in DevicesList.vue)
		setTimeout(() => {
			toast.error(msg, { position: "top-right", duration: 4000 });
		}, 300);
	} finally {
		statusLoading.value = false;
	}
}

function cancelStatusChange() {
	showStatusModal.value = false;
	tempStatus.value = null;
	selectedDevice.value = null;
}

function openStatusModal(device: any) {
	selectedDevice.value = device;
	tempStatus.value = device.is_static;
	showStatusModal.value = true;
}

function closeStatusModal() {
	showStatusModal.value = false;
	tempStatus.value = null;
	selectedDevice.value = null;
}

function extractServerError(err: any): string {
	// prova i campi più comuni del tuo API handler
	return (
		err?.response?.data?.message ||
		err?.response?.data?.details ||
		err?.response?.data?.error ||
		err?.message ||
		"Errore cambio stato"
	);
}

watch(mapCollapsed, async () => {
	await nextTick();
	// aspetta la durata della transition (match al CSS sotto)
	setTimeout(() => {
		try {
			map.value?.leafletObject?.invalidateSize(false);
		} catch {}
	}, 260);
});

onMounted(async () => {
	await loadDevices();
	await fetchAndDecrypt(); // Auto-load static positions (AFTER devices are loaded)
	const t = setInterval(async () => {
		await loadDevices();
		await fetchAndDecrypt();
	}, 10 * 60000);

	onBeforeUnmount(() => {
		clearInterval(t);
	});
});

/* opzionale: chiusura quando il componente si distrugge */
/* opzionale: chiusura quando il componente si distrugge */
onBeforeUnmount(() => {
	socket.off("graph:location");
	socket.off("connect");
	socket.disconnect();
});
</script>

<style>
.modal-mask {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.55);
	backdrop-filter: blur(3px);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9999;
}

.modal {
	min-width: 340px;
	max-width: 90%;
	background: #0d0d0d;
	color: #fff;
	padding: 28px 32px;
	border-radius: 8px;
	box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
	animation: pop 0.25s ease-out;
}

@keyframes pop {
	from {
		opacity: 0;
		transform: scale(0.92);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

.modal-actions {
	margin-top: 26px;
	display: flex;
	justify-content: flex-end;
	gap: 12px;
}

.modal-actions button {
	min-width: 88px;
	padding: 8px 16px;
	font: 600 14px/1 "Inter", sans-serif;
	border-radius: 4px;
	cursor: pointer;
	color: #fff;
	background: #008f8f;
	border: 1px solid #008f8f;
	transition: background 0.15s;
}

.modal-actions button:hover {
	background: #00a7a7;
}

.modal-actions button:active {
	background: #007777;
}

.status-dot {
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	margin-right: 0.5rem; /* spazio tra dot e testo */
	box-shadow: 0 0 0 1px #0d0d0d;
	vertical-align: middle;
}

.status-options {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
	margin-bottom: 20px;
}

.status-options button {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px;
	border: 1px solid #444;
	border-radius: 4px;
	background: #1a1a1a;
	color: #eee;
	cursor: pointer;
	transition: all 0.2s;
}

.status-options button:hover {
	background: #252525;
}

.status-options button.active {
	border-color: #008f8f;
	background: rgba(0, 143, 143, 0.2);
}

.status-options button span {
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 50%;
}

/* icona matita */
.status-edit-icon {
	width: 14px;
	height: 14px;
	stroke: #666;
	opacity: 0; /* invisibile di default…  */
	transition: opacity 0.15s, stroke 0.15s;
}

/* ───────── bottone stato ───────── */
.status-btn {
	display: inline-flex; /* pallino + icona allineati orizzontalmente */
	align-items: center;
	gap: 6px; /* spazio fra pallino e icona */
	background: none;
	border: none;
	cursor: pointer;
	padding: 4px;
	border-radius: 4px;
	transition: background-color 0.2s;
}

.status-btn:hover {
	background-color: rgba(0, 0, 0, 0.06);
}

/* …ma visibile quando il mouse è sulla cella */
.status-btn:hover .status-edit-icon {
	opacity: 1;
}

/* cambio colore se il mouse va proprio sull’icona */
.status-btn:hover .status-edit-icon:hover {
	stroke: #008f8f;
}

.dashboard-wrapper {
	display: flex;
	flex-direction: column;
	/* height: 100%; */
	min-height: 100vh;
	padding: 16px;
	box-sizing: border-box;
	padding-bottom: 24px;
}

.breadcrumb {
	position: sticky;
	top: 0;
	z-index: 10;
	height: 56px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px; /* spazio tra titolo e bottone */
	padding: 0 24px;
	background: #0d0d0d;
	font: 600 18px/56px "Inter", sans-serif;
	/* border-bottom: 2px solid transparent; */
	border-image: linear-gradient(to bottom, #535454, #1d3a3a) 1;

	--bc-bg: #0d0d0d;
	--bc-fg: #e6e6e6;
	--bc-hover: color-mix(in srgb, white 8%, var(--bc-bg)); /* overlay 8% */
	--bc-active: color-mix(in srgb, black 35%, var(--bc-bg)); /* click */
	--bc-border: color-mix(in srgb, white 12%, var(--bc-bg));
}

.breadcrumb span {
	font-size: 20px;
	font-weight: 700;
	color: var(--bc-fg);
}

.map-container {
	position: relative; /* per il FAB */
	flex: 0 0 auto; /* togli blocco fisso, usiamo height */
	height: 400px; /* altezza “aperta” */
	border: 1px solid #ddd;
	border-radius: 8px;
	overflow: hidden;
	margin-bottom: 16px;
	transition: height 0.25s ease, box-shadow 0.25s ease;
}

.map-container.is-collapsed {
	height: 220px; /* altezza “compatta” */
	box-shadow: 0 8px 22px rgba(0, 0, 0, 0.22);
}

/* opzionale: leggero fade ai bordi quando è compressa */
.map-container.is-collapsed::after {
	content: "";
	position: absolute;
	inset: 0;
	pointer-events: none;
	background: linear-gradient(
		to bottom,
		rgba(13, 13, 13, 0) 70%,
		rgba(13, 13, 13, 0.06)
	);
}

/* bottone per tornare su/espandere */
.map-fab {
	position: absolute;
	justify-items: center;
	right: 10px;
	bottom: 10px;
	width: 34px;
	height: 34px;
	border-radius: 50%;
	border: none;
	cursor: pointer;
	font-weight: 700;
	background: #008f8f;
	color: #fff;
	box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
	transition: transform 0.15s;

	z-index: 1000; /* 👈 sopra a tile/marker/tooltip/popup */
	pointer-events: auto; /* assicurati che prenda i click */
}
.map-fab:hover {
	transform: translateY(-1px);
}

.map-refresh-pill {
	position: absolute;
	top: 10px;
	right: 10px;
	z-index: 1000;
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 6px 12px;
	border-radius: 20px;
	border: none;
	background: #008f8f;
	color: #fff;
	font-size: 0.78rem;
	font-weight: 600;
	cursor: pointer;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
	transition: background 0.2s, transform 0.15s, opacity 0.2s;
	pointer-events: auto;
}
.map-refresh-pill:hover:not(:disabled) {
	background: #007a7a;
	transform: translateY(-1px);
}
.map-refresh-pill:disabled {
	opacity: 0.7;
	cursor: default;
}
.map-refresh-pill.updating .pill-label {
	opacity: 0.85;
}
@keyframes spin {
	to { transform: rotate(360deg); }
}
.pill-icon.spin,
.edit-coords-btn svg.spin {
	animation: spin 0.9s linear infinite;
}

.map-container .leaflet-container {
	height: 100%;
	width: 100%;
}

/* .device-popup {
		min-width: 200px;
	}
	.device-popup h4 {
		margin: 0 0 8px 0;
		color: #008f8f;
	}
	.device-popup button {
		margin-top: 8px;
		padding: 4px 8px;
		background: #008f8f;
		color: white;
		border: none;
		border-radius: 3px;
		cursor: pointer;
	} */

/* cursore e highlight al passaggio del mouse */
.device-row {
	cursor: pointer;
}
.device-row:hover {
	background: #d0f2f2 !important;
}
.device-row:focus-visible {
	outline: 2px solid #008f8f;
}

/* ──────── Leaflet popup “shell” ──────── */
.device-frame .leaflet-popup-content-wrapper,
.device-frame .leaflet-popup-content {
	background: transparent !important; /* sparisce il bianco */
	border: none !important;
	margin: 0; /* annulla il padding extra */
	box-shadow: none;
}

/* rimuovi il triangolino che punta al marker */
.device-frame .leaflet-popup-tip-container {
	display: none;
}

/* sposta / restyla il bottone di chiusura */
.device-frame .leaflet-popup-close-button {
	position: absolute;
	top: 6px;
	right: 8px;
	width: 20px;
	height: 20px;
	line-height: 20px;
	text-align: center;
	border-radius: 50%;
	font-size: 16px;
	color: #ccc;
	background: transparent;
	transition: background 0.15s, color 0.15s;
}

/* nasconde del tutto il bottone default */
.device-frame .leaflet-popup-close-button {
	display: none !important;
}
/* Nascondi i tooltip solo quando c'è un popup aperto */
.leaflet-popup-pane:not(:empty) ~ .leaflet-tooltip-pane .marker-tooltip {
	display: none !important;
}

/* .device-frame .leaflet-popup-close-button {
		background-image: url("data:image/svg+xml;utf8,<svg …>");
		background-repeat: no-repeat;
		background-position: center;
		color: transparent;
	}

	.device-frame .leaflet-popup-close-button:hover {
		background: #00a7a7;
		color: #fff;
	} */

/* facoltativo: ombra al “guscio” intero */
.device-frame {
	filter: drop-shadow(0 8px 20px rgba(0, 0, 0, 0.25));
}

/* Stili per il cluster */
.marker-cluster-small {
	background-color: rgba(181, 226, 140, 0.6);
}
.marker-cluster-small div {
	background-color: rgba(110, 204, 57, 0.6);
}

.marker-cluster-medium {
	background-color: rgba(241, 211, 87, 0.6);
}
.marker-cluster-medium div {
	background-color: rgba(240, 194, 12, 0.6);
}

.marker-cluster-large {
	background-color: rgba(253, 156, 115, 0.6);
}
.marker-cluster-large div {
	background-color: rgba(241, 128, 23, 0.6);
}

.marker-cluster {
	background-clip: padding-box;
	border-radius: 20px;
}
.marker-cluster div {
	width: 30px;
	height: 30px;
	margin-left: 5px;
	margin-top: 5px;
	text-align: center;
	border-radius: 15px;
	font: 12px "Helvetica Neue", Arial, Helvetica, sans-serif;
	font-weight: bold;
}

/* Stili per il tooltip */
.marker-tooltip {
	background: #0d0d0d;
	color: #008f8f;
	border: none;
	border-radius: 4px;
	padding: 6px 10px;
	font: 12px/1.2 "Inter", sans-serif;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
	pointer-events: none;
}

.marker-tooltip:before {
	border-top-color: #0d0d0d;
}

/* alone tondo pulsante per i device dinamici+online */
.leaflet-marker-pane .marker-movable {
	/* evita qualsiasi spostamento del pin */
	transform: translateZ(0);
	will-change: filter;
	animation: haloPulse 1.9s ease-in-out infinite;
}

@keyframes haloPulse {
	0%,
	100% {
		/* nessun alone */
		filter: drop-shadow(0 0 0 rgba(0, 143, 143, 0))
			drop-shadow(0 0 0 rgba(0, 143, 143, 0))
			drop-shadow(0 0 0 rgba(0, 143, 143, 0));
	}
	50% {
		/* alone circolare morbido (anelli concentrici) */
		filter: drop-shadow(0 0 6px rgba(0, 143, 143, 0.45))
			drop-shadow(0 0 12px rgba(0, 143, 143, 0.35))
			drop-shadow(0 0 18px rgba(0, 143, 143, 0.22));
	}
}

/* Stili tabella */
.tbl-wrap {
	margin-top: 50px;
	border: 1px solid #ddd;
	border-radius: 4px;
	overflow-x: auto;
}

.tbl {
	width: 100%;
	border-collapse: collapse;
	font: 14px "Inter", sans-serif;
}

th,
td {
	padding: 10px 16px;
	text-align: left;
	white-space: nowrap;
}

thead {
	font-weight: 600;
}

.sortable {
	cursor: pointer;
	user-select: none;
	transition: background-color 0.2s;
}

.sortable:hover {
	background-color: #f0f0f0;
}

.header-content {
	display: flex;
	align-items: center;
	gap: 8px;
}

.sort-icon {
	width: 14px !important;
	height: 14px !important;
	color: #008f8f;
}

.tbl tbody tr:nth-child(odd) {
	background: #f5f5f5;
}

.tbl tbody tr:nth-child(even) {
	background: #fff;
}

.tbl tbody tr:not(.search-row):hover {
	background: #e0e0e0 !important;
}

/* coord compatte e monospazio */
td.mono {
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
		"Liberation Mono", "Courier New", monospace;
}
.coords {
	cursor: pointer;
}
.coords:hover {
	text-decoration: underline;
}
.edit-coords-btn {
	appearance: none;
	background: none;
	border: none;
	color: #6b7280;
	cursor: pointer;
	padding: 2px 4px;
	margin-left: 4px;
	border-radius: 4px;
	vertical-align: middle;
	line-height: 1;
}
.edit-coords-btn:hover {
	color: #22c55e;
	background: rgba(34, 197, 94, 0.1);
}
.on {
	background: #27ae60;
}
.off {
	background: #e74c3c;
}
.maintenance {
	background: #f39c12;
	animation: pulse 2s infinite;
}
.error {
	background: #e74c3c; /* Red equivalent to .off */
	animation: pulse 1s infinite;
}

@keyframes pulse {
	0% {
		opacity: 1;
	}
	50% {
		opacity: 0.5;
	}
	100% {
		opacity: 1;
	}
}

.msg {
	margin: 24px 0;
	color: #666;
	text-align: center;
	padding: 16px;
	background: #f5f5f5;
	border-radius: 4px;
}

.error {
	color: #d00;
}

@media (max-width: 1023px) {
	.breadcrumb {
		padding: 0 45px;
	}
}

/* Riga contenitore delle etichette */
.label-row {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

/* Chip stile Gmail */
.label {
	display: inline-flex;
	align-items: center;
	padding: 2px 8px;
	border: 1px solid currentColor;
	border-radius: 9px;
	font: 600 12px/1.6 "Inter", sans-serif;
	background: transparent;
	user-select: none;
	white-space: nowrap;
}

/* Palette (bg molto chiaro + testo/bordo colorati) */
.label--motherboard {
	color: #2c3e50;
	background: #ecf0f1;
}
.label--lora {
	color: #0b8457;
	background: #e8f7f1;
}
.label--lte5g {
	color: #0a58ca;
	background: #e7f0ff;
}
.label--sat {
	color: #a15a00;
	background: #fff3e0;
}
.label--wifi {
	color: #5d2ca8;
	background: #f1e9ff;
}
.label--ble {
	color: #00796b;
	background: #e0f2f1;
}
.label--gps {
	color: #00695c;
	background: #e0f7f6;
}
.label--camera {
	color: #b71c1c;
	background: #ffebee;
}
.label--ai {
	color: #6a1b9a;
	background: #f3e5f5;
}
.label--battery {
	color: #2e7d32;
	background: #e8f5e9;
}
.label--solar {
	color: #ef6c00;
	background: #fff3e0;
}
.label--storage {
	color: #455a64;
	background: #eceff1;
}

/* fallback per moduli sconosciuti */
.label-- {
	color: #444;
	background: #f2f2f2;
}

/* Badge per device condivisi */
.shared-device-badge {
	display: inline-block;
	margin-left: 8px;
	padding: 3px 8px;
	font-size: 11px;
	font-weight: 600;
	color: #00838f;
	background: #e0f7fa;
	border: 1px solid #00acc1;
	border-radius: 12px;
	vertical-align: middle;
}

/* Azioni nella tabella Dashboard */
.tbl td.actions {
	text-align: right;
	vertical-align: middle;
	white-space: nowrap;
}

.btn-action-labeled {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background-color: #f0f2f5 !important;
	border-radius: 6px;
	padding: 4px 10px !important;
	margin-right: 4px;
	font-size: 0.8rem;
	font-weight: 600;
	color: #444;
	transition: all 0.2s ease;
	border: 1px solid #dee2e6;
	cursor: pointer;
}
.status-icon {
	width: 16px;
	height: 16px;
	margin-right: 6px;
	vertical-align: middle;
	display: inline-block;
}

.status-icon.static {
	color: #ff4d4f; /* Red-ish for Static/Off */
}

.status-icon.dynamic {
	color: #52c41a; /* Green-ish for Dynamic/On */
}

/* Update modal buttons to align icon */
.status-options button {
	display: flex;
	align-items: center;
	justify-content: center;
}

.status-options .status-icon {
	width: 20px;
	height: 20px;
	margin-right: 8px;
}

.btn-action-labeled:hover {
	background-color: #008f8f !important;
	color: #fff;
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 143, 143, 0.2);
	border-color: #008f8f;
}

.btn-action-labeled svg {
	width: 14px;
	height: 14px;
}

.btn-action-labeled:hover svg {
	stroke: #fff;
}

/* Ricerca */
.search-row {
	background: transparent !important;
}

.search-container {
	padding: 12px 16px;
	background: #f8f8f8;
	border-bottom: 1px solid #ddd;
}

.search-input {
	width: 100%;
	max-width: 400px;
	padding: 8px 12px;
	border: 1px solid #ccc;
	border-radius: 6px;
	font-size: 14px;
	background: #fff;
	color: #333;
	transition: border-color 0.2s;
}

.search-input:focus {
	outline: none;
	border-color: #008f8f;
	box-shadow: 0 0 0 2px rgba(0, 143, 143, 0.1);
}

/* Paginazione */
.pagination-controls {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 20px;
	margin-top: 24px;
	padding: 16px;
}

.btn-pagination {
	padding: 6px 16px;
	/* border: 1px solid #008f8f; */
	background: #008f8f;
	color: #fff;
	border-radius: 4px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s;
}

.btn-pagination:hover:not(:disabled) {
	background: #00a7a7;
	/* color: #fff; */
}

.btn-pagination:disabled {
	background-color: #ccc;
	/* border-color: #0d0d0d; */
	color: #fff;
	cursor: not-allowed;
}

.pagination-info {
	font-size: 14px;
	color: #666;
	font-weight: 500;
}

.pagination-jump {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 14px;
	color: #666;
	font-weight: 500;
}

.jump-input {
	width: 50px;
	padding: 4px 8px;
	border: 1px solid #ccc;
	border-radius: 4px;
	text-align: center;
	font-size: 14px;
	outline: none;
}

.jump-input:focus {
	border-color: #008f8f;
}

/* Rimuovi freccine input number */
.jump-input::-webkit-outer-spin-button,
.jump-input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	appearance: none;
	margin: 0;
}
.jump-input[type="number"] {
	-moz-appearance: textfield;
	appearance: textfield;
}
</style>
